import { AxiosRequestConfig, Method } from 'axios';
import axiosInstance from './index';
import { NO_INTERNET_ERR, FALLBACK_ERR } from '../constants/index';
// @ts-ignore
import { isInternetReachable } from '../utils/netInfo';

export interface ErrorResponse {
  isOffline?: boolean;
  status?: number;
  message?: string;
  // Present only for the "coded" error shape {"detail": {"code", "message", ...}},
  // used by the partner routes, e.g. "PARTNER_NOT_FOUND", "INVALID_TRANSITION".
  code?: string;
  // Extra fields some coded errors carry: attempts_left, retry_after_seconds, current_status
  extra?: Record<string, any>;
  rawError?: any;
}

/**
 * Custom exception class to satisfy 'no-throw-literal' lint rule
 * while maintaining the ErrorResponse structure.
 */
export class ApiException extends Error {
  isOffline?: boolean;

  status?: number;

  code?: string;

  extra?: Record<string, any>;

  rawError?: any;

  constructor(error: ErrorResponse) {
    super(error.message || FALLBACK_ERR);
    this.isOffline = error.isOffline;
    this.status = error.status;
    this.code = error.code;
    this.extra = error.extra;
    this.rawError = error.rawError;
    Object.setPrototypeOf(this, ApiException.prototype);
  }
}

const parseError = (err: any): ErrorResponse => {
  if (err.response) {
    let msg = err.response.data?.message;
    let code: string | undefined;
    let extra: Record<string, any> | undefined;
    const detail = err.response.data?.detail;
    if (!msg && detail) {
      if (typeof detail === 'string') {
        msg = detail;
      } else if (Array.isArray(detail)) {
        msg = detail.map((d: any) => d.msg || JSON.stringify(d)).join(', ');
      } else if (detail && typeof detail.message === 'string') {
        // Coded shape: {"detail": {"code", "message", ...extra}}
        const { code: detailCode, message, ...rest } = detail;
        msg = message;
        code = detailCode;
        if (Object.keys(rest).length) extra = rest;
      } else {
        msg = JSON.stringify(detail);
      }
    }
    return {
      status: err.response.status,
      message: msg || FALLBACK_ERR,
      code,
      extra,
      rawError: err,
    };
  }
  return {
    message: err.message || FALLBACK_ERR,
    rawError: err,
  };
};

const apiExecutor = async (
  method: Method,
  endpoint: string,
  data?: any,
  params?: any,
  extraConfig?: AxiosRequestConfig
): Promise<any> => {
  const isOnline = await isInternetReachable();
  if (!isOnline) {
    throw new ApiException({ isOffline: true, message: NO_INTERNET_ERR });
  }

  try {
    const config: AxiosRequestConfig = {
      method,
      url: endpoint,
      data,
      params,
      ...extraConfig,
    };
    const response = await axiosInstance.request(config);
    return response.data;
  } catch (err: any) {
    throw new ApiException(parseError(err));
  }
};

export const getApi = (endpoint: string, params?: any): Promise<any | ErrorResponse> =>
  apiExecutor('GET', endpoint, null, params);

// For binary responses (e.g. images behind the auth header)
export const getBlobApi = (endpoint: string, params?: any): Promise<Blob> =>
  apiExecutor('GET', endpoint, null, params, { responseType: 'blob' });

export const postApi = (endpoint: string, data?: any, params?: any): Promise<any | ErrorResponse> =>
  apiExecutor('POST', endpoint, data, params);

export const patchApi = (endpoint: string, data?: any, params?: any): Promise<any | ErrorResponse> =>
  apiExecutor('PATCH', endpoint, data, params);

export const putApi = (endpoint: string, data?: any): Promise<any | ErrorResponse> =>
  apiExecutor('PUT', endpoint, data);

